<script lang="ts" setup>
import { useDefaultStore } from "@/store";
import api from "@/api";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { debounce } from "@/assets/js/helpers";
import { useConfirm } from "primevue/useconfirm";
import type { IUserList, IPermissions } from "@/api/types";
import { useSessionStore } from "@/store/session";

definePage({
	name: "ManageUsers",
	meta: {
		name: "User Management",
	},
});

const confirm = useConfirm();
const toast = useToast();
const store = useDefaultStore();
const sessionStore = useSessionStore();
const searchValue = ref<string | null>(null);
const numRows = ref(8);
const offsetValue = ref<number | null>(0);
const currentPage = ref(1);
const selectedProduct = ref();
const selectedUserStatus = ref<string | null>(null);
const loadingDelete = ref(false);
const users = ref<any | null>(null);

async function getUsers() {
	if (searchValue.value && !searchValue.value.trim()) searchValue.value = null;

	const params = {
		search: searchValue.value,
		limit: numRows.value,
		offset: offsetValue.value,
		usr_status: selectedUserStatus.value,
		list: true,
	};

	try {
		const res = await api.usersGet(params);
		users.value = res.data.data;
		users.value = res.data.data.map((user: IUserList) => {
			// Map status name
			const status = store.userStatuses.find(
				(s) => s.value === user.usr_status,
			);
			user.usr_status_label = status ? status.label : user.usr_status;

			user.roles = JSON.parse(user.roles);
			user.permissions = JSON.parse(user.permissions);

			return user;
		});
		// console.log(users.value);
	} catch (err: any) {
		console.warn(err.message);
	}
}

async function onPageChange(event: any) {
	currentPage.value = event.page + 1;
	offsetValue.value = numRows.value * currentPage.value - numRows.value;
	await getUsers();
}

const getPostsDebounce = () => {
	debounce(getUsers(), 300, true);
};

async function selectStatus(event: any) {
	selectedUserStatus.value = null;
	if (event) {
		selectedUserStatus.value = event.value;
	}
	await getUsers();
}

async function deleteUser(usr_id: number) {
	loadingDelete.value = true;

	const params = {
		usr_id,
	};

	try {
		const res = await api.usersRemove(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageUsers.success.deleteUser.title"),
			detail: $t("app.manageUsers.success.deleteUser.msg"),
			life: 3000,
		});
		await getUsers();
		console.log(res.data.data);
	} catch (err: any) {
		if (
			err.response.data.message.includes("not integer number") ||
			err.response.data.message.includes("required")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.manageUsers.error.invalidId.title"),
				detail: $t("app.manageUsers.error.invalidId.msg"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("Token has been blacklisted.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("User not found")) {
			toast.add({
				severity: "error",
				summary: $t("app.manageUsers.error.notFound.title"),
				detail: $t("app.manageUsers.error.notFound.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	} finally {
		loadingDelete.value = false;
	}
}

const confirmDelete = (usr_id: number) => {
	confirm.require({
		message: $t("app.manageUsers.deleteUser.msg"),
		header: $t("app.manageUsers.deleteUser.header"),
		rejectLabel: $t("app.basic.cancel"),
		rejectProps: {
			label: $t("app.basic.cancel"),
			severity: "secondary",
			outlined: true,
		},
		acceptProps: {
			label: $t("app.basic.delete"),
			severity: "danger",
		},
		accept: () => {
			deleteUser(usr_id);
		},
	});
};

const confirmChangeStatus = (user: IUserList) => {
	confirm.require({
		message:
			user.usr_status === store.userStatuses[0].value
				? $t("app.manageUsers.changeStatus.msg")
				: $t("app.manageUsers.changeStatus.msgBanned"),
		header:
			user.usr_status === store.userStatuses[0].value
				? $t("app.manageUsers.changeStatus.header")
				: $t("app.manageUsers.changeStatus.headerBanned"),
		rejectProps: {
			label: $t("app.basic.cancel"),
			severity: "secondary",
			outlined: true,
		},
		acceptProps: {
			label: $t("app.basic.confirm"),
			severity:
				user.usr_status === store.userStatuses[0].value ? "danger" : "info",
		},
		accept: () => {
			editUser(
				user.usr_id,
				user.usr_status === store.userStatuses[0].value
					? store.userStatuses[1].value
					: store.userStatuses[0].value,
			);
		},
	});
};

async function editUser(
	usr_id: number,
	usr_status: string | null = null,
	rol_ids: number[] | null = null,
	per_ids: number[] | null = null,
) {
	const params = {
		usr_id: usr_id,
		usr_status: usr_status,
		rol_ids: rol_ids ? JSON.stringify(rol_ids) : undefined,
		per_ids: per_ids ? JSON.stringify(per_ids) : undefined,
	};

	try {
		const res = await api.usersEdit(params);
		await getUsers();
		if (params.usr_status) {
			toast.add({
				severity: "success",
				summary:
					params.usr_status === store.userStatuses[0].value
						? $t("app.manageUsers.success.changeStatus.title")
						: $t("app.manageUsers.success.changeStatus.titleBanned"),
				detail:
					params.usr_status === store.userStatuses[0].value
						? $t("app.manageUsers.success.changeStatus.msg")
						: $t("app.manageUsers.success.changeStatus.msgBanned"),
				life: 3000,
			});
		}
		if (params.rol_ids) {
			toast.add({
				severity: "success",
				summary: $t("app.manageUsers.success.changeRole.title"),
				detail: $t("app.manageUsers.success.changeRole.msg"),
				life: 3000,
			});
		}
		if (params.per_ids) {
			toast.add({
				severity: "success",
				summary: $t("app.manageUsers.success.changePermission.title"),
				detail: $t("app.manageUsers.success.changePermission.msg"),
				life: 3000,
			});
		}
		console.warn(res.data);
	} catch (err: any) {
		if (
			err.response.data.message.includes("User not found.") ||
			err.response.data.message.includes("Column 'usr_id' cannot be null")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.userNotFound.title"),
				detail: $t("app.userEdit.error.userNotFound.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("No update params.")) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.noUpdateParams.title"),
				detail: $t("app.userEdit.error.noUpdateParams.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("is expected to be array")) {
			if (params.rol_ids) {
				toast.add({
					severity: "error",
					summary: $t("app.manageUsers.error.invalidRole.title"),
					detail: $t("app.manageUsers.error.invalidRole.msg"),
					life: 3000,
				});
			} else if (params.per_ids) {
				toast.add({
					severity: "error",
					summary: $t("app.permissions.editPermission.title"),
					detail: $t("app.permissions.editPermission.msg"),
					life: 3000,
				});
			}
		} else if (
			err.response.data.message.includes("Token has been blacklisted.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	}
}

const isOptionDisabledRoles = (option) => {
	return option.rol_label === store.roles[0].rol_label;
};

async function changeUserRole(event, usr_id) {
	if (!sessionStore.can("edit-user-roles")) {
		toast.add({
			severity: "error",
			summary: $t("app.permissions.editRoles.title"),
			detail: $t("app.permissions.editRoles.msg"),
			life: 3000,
		});
		return;
	}
	await editUser(usr_id, null, [event.rol_id], null);
}

async function changeUserPermission(event: IPermissions[], usr_id) {
	if (!sessionStore.can("edit-user-permissions")) {
		toast.add({
			severity: "error",
			summary: $t("app.permissions.invalidPermissions.title"),
			detail: $t("app.permissions.invalidPermissions.msg"),
			life: 3000,
		});
		return;
	}
	const perIds = ref([]);
	for (let i = 0; i < event.length; i++) {
		const permission = event[i];
		perIds.value.push(permission.per_id);
	}
	// console.log(perIds.value);
	await editUser(usr_id, null, null, perIds.value);
}

onMounted(async () => {
	await getUsers();
});
</script>

<template>
	<div class="admin-section-outer-wrapper">
		<div class="admin-section-inner-wrapper">
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.manageUsers.title") }}
				</div>
				<div class="admin-section-header-filters-wrapper">
					<IconField
						:pt="{
							root: {
								style: 'width: 333px; max-width: 90%; height: 38px;',
							},
						}"
					>
						<InputIcon>
							<i-fa-magnifying-glass />
						</InputIcon>
						<InputText
							v-model="searchValue"
							@value-change="getPostsDebounce"
							:placeholder="$t('app.manageUsers.searchPlaceholder')"
							fluid
						/>
					</IconField>
					<Select
						id="user-status"
						:options="store.userStatuses"
						optionLabel="label"
						:placeholder="$t('app.manageUsers.statusPlaceholder')"
						:showClear="true"
						:resetFilterOnClear="true"
						:showToggleAll="false"
						@update:modelValue="selectStatus"
						:pt="{
							root: {
								class: 'manage-users-filters',
							},
							label: {
								style: 'padding-right: 24px',
							},
						}"
					/>
					<create-user
						v-if="sessionStore.can('create-user')"
						@create-user="getUsers()"
					/>
				</div>
			</div>
			<div class="admin-section-content-wrapper">
				<DataTable
					v-if="!users"
					:value="[
						{ id: 1 },
						{ id: 2 },
						{ id: 3 },
						{ id: 4 },
						{ id: 5 },
						{ id: 6 },
						{ id: 7 },
						{ id: 8 },
					]"
					tableStyle="min-width: 1264px;"
					stripedRows
				>
					<Column :header="$t('app.manageUsers.fullNameLabel')">
						<template #body>
							<Skeleton height="37px"></Skeleton>
						</template>
					</Column>
					<Column
						:header="$t('app.manageUsers.emailLabel')"
						style="width: 300px"
					>
						<template #body>
							<Skeleton></Skeleton>
						</template>
					</Column>
					<Column
						:header="$t('app.manageUsers.changeRoleLabel')"
						style="width: 200px"
					>
						<template #body>
							<Skeleton></Skeleton>
						</template>
					</Column>
					<Column
						:header="$t('app.manageUsers.changePermissionsLabel')"
						style="max-width: 240px; width: 240px"
					>
						<template #body>
							<Skeleton></Skeleton>
						</template>
					</Column>
					<Column
						:header="$t('app.manageUsers.changeStatusLabel')"
						style="width: 100px"
					>
						<template #body>
							<Button
								:label="store.userStatuses[0].value"
								severity="info"
								variant="outlined"
								style="
									width: 92px;
									height: 29px;
									font-size: 11px;
									font-weight: 700;
									line-height: 15px;
								"
							/>
						</template>
					</Column>
					<Column
						:header="$t('app.basic.delete')"
						style="width: 100px"
					>
						<template #body>
							<Button
								:label="$t('app.basic.delete')"
								severity="danger"
								variant="outlined"
								style="
									width: 92px;
									height: 29px;
									font-size: 11px;
									font-weight: 700;
									line-height: 15px;
								"
							/>
						</template>
					</Column>
				</DataTable>
				<DataTable
					v-else
					:value="users"
					tableStyle="min-width: 1264px;"
					stripedRows
					selectionMode="single"
					v-model:selection="selectedProduct"
				>
					<Column field="usr_profile_picture">
						<template #header>
							<div style="width: 100%; text-align: center">
								<i-fa-user></i-fa-user>
							</div>
						</template>
						<template #body="{ data }">
							<div style="width: 100%; text-align: center">
								<Avatar
									:image="data.profile_picture || ''"
									:label="
										!data.profile_picture
											? store.getFirstLetter(data.usr_displayname)
											: ''
									"
									shape="circle"
									style="
										background-color: rgba(44, 117, 244, 0.231);
										color: rgb(41, 41, 41);
									"
								/>
							</div>
						</template>
					</Column>
					<Column
						field="usr_displayname"
						:header="$t('app.manageUsers.fullNameLabel')"
					></Column>
					<Column
						field="usr_email"
						:header="$t('app.manageUsers.emailLabel')"
						style="width: 300px"
					></Column>
					<Column
						:header="$t('app.manageUsers.changeRoleLabel')"
						style="width: 200px"
					>
						<template #body="{ data }">
							<Select
								v-if="data.roles[0].rol_label !== store.roles[0].rol_label"
								id="status"
								v-model="data.roles[0]"
								:options="store.roles"
								optionLabel="rol_label"
								@update:modelValue="changeUserRole($event, data.usr_id)"
								:pt="{
									root: {
										style: 'width: 100%;',
									},
								}"
								:optionDisabled="isOptionDisabledRoles"
							/>
							<div v-else>{{ store.roles[0].rol_label }}</div>
						</template>
					</Column>
					<Column
						:header="$t('app.manageUsers.changePermissionsLabel')"
						style="max-width: 240px; width: 240px"
					>
						<template #body="{ data }">
							<MultiSelect
								id="status"
								:options="store.permissions"
								:modelValue="data.permissions"
								optionLabel="per_label"
								:placeholder="$t('app.manageUsers.changePermissionsLabel')"
								:maxSelectedLabels="1"
								:showToggleAll="false"
								@update:modelValue="changeUserPermission($event, data.usr_id)"
								:pt="{
									root: {
										style: 'width: 100%',
									},
								}"
								:disabled="data.roles[0].rol_label === store.roles[0].rol_label"
							/>
						</template>
					</Column>
					<Column
						:header="$t('app.manageUsers.changeStatusLabel')"
						style="width: 100px"
					>
						<template #body="{ data }">
							<Button
								:label="data.usr_status_label"
								:severity="
									data.usr_status === store.userStatuses[0].value
										? 'info'
										: 'danger'
								"
								variant="outlined"
								style="
									width: 92px;
									height: 29px;
									font-size: 11px;
									font-weight: 700;
									line-height: 15px;
								"
								:disabled="data.roles[0].rol_label === store.roles[0].rol_label"
								@click="confirmChangeStatus(data)"
							/>
						</template>
					</Column>
					<Column
						:header="$t('app.basic.delete')"
						style="width: 100px"
					>
						<template #body="{ data }">
							<Button
								:label="$t('app.basic.delete')"
								severity="danger"
								variant="outlined"
								style="
									width: 92px;
									height: 29px;
									font-size: 11px;
									font-weight: 700;
									line-height: 15px;
								"
								@click="confirmDelete(data.usr_id)"
								:loading="loadingDelete"
								:disabled="data.roles[0].rol_label === store.roles[0].rol_label"
							/>
						</template>
					</Column>
				</DataTable>
				<Paginator
					v-if="users && users.length > 0"
					:template="{
						'768px': 'PrevPageLink CurrentPageReport NextPageLink',
						default:
							'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink',
					}"
					:rows="numRows"
					:totalRecords="users[0].total_count"
					@page="onPageChange"
				></Paginator>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-datatable-table-container {
	&::-webkit-scrollbar {
		background-color: $scrollbar-color;
		width: 5px;
		height: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $scrollbar-color-thumb;
		border-radius: 5px;
	}
}

.p-datatable-column-title {
	font-size: 11px;
	font-weight: 700;
	line-height: 12.89px;
	color: $text-color-lighter;
}

.manage-users-filters {
	width: 333px !important;
	max-width: 90% !important;

	@media screen and (width >= 1224px) {
		width: 173px !important;
	}
}
</style>
